import { generateInviteLink, shareInviteViaEmail } from '@/infrastructure/sdk';
import { useLanguage } from '@/localization/useLanguage';
import { getLocalUserId, blockOfflineAction } from '@/shared/utils';
import { showErrorToast, showInfoToast, showSuccessToast } from '@/shared/utils/toastMessage';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectGeneratedInviteLink, setGeneratedLink } from '../model';

const EMAIL_SEPARATOR_REGEX = /[\s,;]+/;
const EMAIL_VALIDATION_REGEX = /^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,63}\.[a-zA-Z]{2,}$/;
const COMMIT_KEYS = new Set(['Enter', ',', ';']);

export const INVITE_FLOW_STEP = Object.freeze({
   OPTIONS: 'options',
   SHARE: 'share',
   EMAIL: 'email',
});

const INITIAL_STATE = {
   currentStep: INVITE_FLOW_STEP.OPTIONS,
   emailDraft: '',
   emailRecipients: [],
   invalidEmailEntries: [],
   failedEmailEntries: [],
   isLoadingInviteLink: false,
   isSendingInvites: false,
};

const POST_SEND_RESET = {
   emailRecipients: [],
   emailDraft: '',
   invalidEmailEntries: [],
   failedEmailEntries: [],
};

const normalizeEmail = email => email.trim().toLowerCase();
const isValidEmail = email => EMAIL_VALIDATION_REGEX.test(email);
const toEmailTokens = value => value.split(EMAIL_SEPARATOR_REGEX).map(normalizeEmail).filter(Boolean);
const uniqueMerge = (existing, incoming) => [...new Set([...existing, ...incoming])];

const parseInviteLinkResponse = response => ({
   inviteLink: response?.generate_link ?? response?.inviteLink ?? response?.generatedLink ?? null,
   expiryTime: response?.expiry_time ?? response?.expiryTime ?? null,
});

/**
 * Parses the current draft and merges valid addresses into the recipient list.
 * Returns null when the draft is empty or nothing actually changed,
 * so callers can skip a state write entirely.
 */
const resolveCommittedState = (draft, existingRecipients, currentUserEmail) => {
   const tokens = toEmailTokens(draft);

   if (!tokens.length) return null;

   const self = normalizeEmail(currentUserEmail || '');
   const hasSelf = tokens.some(e => e === self);
   const valid = tokens.filter(e => isValidEmail(e) && e !== self);
   const invalid = tokens.filter(e => !isValidEmail(e));
   const merged = uniqueMerge(existingRecipients, valid);

   if (!hasSelf && !invalid.length && merged.length === existingRecipients.length) return null;

   return {
      emailRecipients: merged,
      invalidEmailEntries: invalid,
      failedEmailEntries: [],
      emailDraft: invalid.join(', '),
      hasSelf, // consumed by callers, not stored in flowState
   };
};

export const useInviteFlow = onClose => {
   const dispatch = useDispatch();
   const { t } = useLanguage();
   const inviteLink = useSelector(selectGeneratedInviteLink);
   const localUserId = getLocalUserId();
   const currentUser = useSelector(state => state.userInfoData.users[localUserId]);
   const currentUserEmail = currentUser?.email;
   const currentUserName = currentUser?.nickName || currentUser?.name || t('common.UNKNOWN_CONTACT');
   const AppName = t('eim.TITLE');

   const [flowState, setFlowState] = useState(INITIAL_STATE);

   const mergeFlowState = useCallback(patch => {
      setFlowState(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
   }, []);

   const ensureInviteLink = useCallback(async () => {
      if (inviteLink) return inviteLink;
      if (flowState.isLoadingInviteLink) return null;

      mergeFlowState({ isLoadingInviteLink: true });
      try {
         const response = await generateInviteLink();
         const parsed = parseInviteLinkResponse(response);

         if (!parsed.inviteLink) {
            showErrorToast(response?.message ?? t('toast.SOMETHING_WENT_WRONG_RETRY'));
            return null;
         }
         dispatch(setGeneratedLink(parsed));
         return parsed.inviteLink;
      } catch (error) {
         showErrorToast(error?.message ?? t('toast.SOMETHING_WENT_WRONG_RETRY'));
         return null;
      } finally {
         mergeFlowState({ isLoadingInviteLink: false });
      }
   }, [dispatch, inviteLink, flowState.isLoadingInviteLink, mergeFlowState, t]);

   const formatInviteMessage = useCallback(
      link =>
         t('invite.SHARE_TEMPLATE', {
            SenderName: currentUserName,
            AppName,
            InviteLink: link,
         }),
      [currentUserName, AppName, t],
   );

   const copyInviteLink = useCallback(async () => {
      const link = await ensureInviteLink();
      if (!link) return;
      const fullMessage = formatInviteMessage(link);
      try {
         await navigator.clipboard.writeText(fullMessage);
         showSuccessToast(t('common.LINK_COPIED'));
      } catch {
         showInfoToast(t('invite.CLIPBOARD_UNAVAILABLE'));
      }
   }, [ensureInviteLink, formatInviteMessage, t]);

   const commitDraftEmails = useCallback(() => {
      const patch = resolveCommittedState(flowState.emailDraft, flowState.emailRecipients, currentUserEmail);
      if (!patch) return;
      if (patch.hasSelf) showErrorToast(t('invite.CANNOT_ADD_SELF'));
      mergeFlowState(patch);
   }, [flowState.emailDraft, flowState.emailRecipients, currentUserEmail, mergeFlowState, t]);

   const sendEmailInvites = useCallback(async () => {
      if (blockOfflineAction()) return;
      if (flowState.isSendingInvites) return;

      const patch = resolveCommittedState(flowState.emailDraft, flowState.emailRecipients, currentUserEmail);
      const finalRecipients = patch?.emailRecipients ?? flowState.emailRecipients;

      if (patch?.hasSelf) showErrorToast(t('invite.CANNOT_ADD_SELF'));

      if (!finalRecipients.length) {
         if (patch) mergeFlowState(patch);
         return;
      }

      // Skip the chip-commit re-render — render only the loading state so a typed
      // address doesn't flash as a chip before the modal closes on success.
      mergeFlowState({ isSendingInvites: true });

      const link = await ensureInviteLink();
      if (!link) {
         mergeFlowState({ isSendingInvites: false });
         return;
      }

      try {
         const response = await shareInviteViaEmail(finalRecipients, link);
         const failedEmails = response?.failed_emails ?? [];

         if (response?.status === false) {
            showErrorToast(response?.message ?? t('toast.SOMETHING_WENT_WRONG_RETRY'));
            if (patch) mergeFlowState(patch);
            return;
         }

         if (failedEmails.length) {
            // Partial success — keep failed addresses visible so the user can retry.
            const successCount = finalRecipients.length - failedEmails.length;
            if (successCount > 0) showSuccessToast(t('invite.SEND_PARTIAL_SUCCESS', { count: successCount }));
            mergeFlowState({
               emailRecipients: failedEmails,
               failedEmailEntries: failedEmails,
               emailDraft: patch?.emailDraft ?? flowState.emailDraft,
               invalidEmailEntries: patch?.invalidEmailEntries ?? flowState.invalidEmailEntries,
            });
            return;
         }

         showSuccessToast(t('invite.SEND_SUCCESS'));

         const hasInvalidDraft = (patch?.invalidEmailEntries ?? flowState.invalidEmailEntries).length > 0;
         if (hasInvalidDraft) {
            mergeFlowState({
               emailRecipients: [],
               emailDraft: patch?.emailDraft ?? flowState.emailDraft,
               invalidEmailEntries: patch?.invalidEmailEntries ?? flowState.invalidEmailEntries,
            });
         } else {
            mergeFlowState(POST_SEND_RESET);
            onClose?.();
         }
      } catch (error) {
         showErrorToast(error?.message ?? t('toast.SOMETHING_WENT_WRONG_RETRY'));
         if (patch) mergeFlowState(patch);
      } finally {
         mergeFlowState({ isSendingInvites: false });
      }
   }, [
      currentUserEmail,
      ensureInviteLink,
      flowState.emailDraft,
      flowState.emailRecipients,
      flowState.isSendingInvites,
      flowState.invalidEmailEntries,
      mergeFlowState,
      onClose,
      t,
   ]);

   const handleEmailInputChange = useCallback(
      value => mergeFlowState({ emailDraft: value, invalidEmailEntries: [], failedEmailEntries: [] }),
      [mergeFlowState],
   );

   const handleEmailInputKeyDown = useCallback(
      event => {
         if (COMMIT_KEYS.has(event.key)) {
            event.preventDefault();
            commitDraftEmails();
         }
      },
      [commitDraftEmails],
   );

   const handleRemoveEmail = useCallback(
      email =>
         mergeFlowState(prev => ({
            emailRecipients: prev.emailRecipients.filter(r => r !== email),
         })),
      [mergeFlowState],
   );

   const canSend = useMemo(
      () =>
         !flowState.isSendingInvites &&
         (flowState.emailRecipients.length > 0 || toEmailTokens(flowState.emailDraft).some(isValidEmail)),
      [flowState.isSendingInvites, flowState.emailRecipients, flowState.emailDraft],
   );

   const stepViewModels = useMemo(
      () => ({
         [INVITE_FLOW_STEP.OPTIONS]: {
            isLoading: flowState.isLoadingInviteLink,
            onOpenShareStep: async () => {
               const link = await ensureInviteLink();
               if (link) mergeFlowState({ currentStep: INVITE_FLOW_STEP.SHARE });
            },
            onOpenEmailStep: async () => {
               const link = await ensureInviteLink();
               if (link) mergeFlowState({ currentStep: INVITE_FLOW_STEP.EMAIL });
            },
         },
         [INVITE_FLOW_STEP.SHARE]: {
            inviteLink,
            inviteMessage: inviteLink ? formatInviteMessage(inviteLink) : '',
            isLoading: flowState.isLoadingInviteLink,
            onCopyLink: copyInviteLink,
            onClose,
         },
         [INVITE_FLOW_STEP.EMAIL]: {
            emails: flowState.emailRecipients,
            emailInput: flowState.emailDraft,
            invalidEmails: flowState.invalidEmailEntries,
            failedEmails: flowState.failedEmailEntries,
            isLoading: flowState.isSendingInvites,
            canSend,
            onInputChange: handleEmailInputChange,
            onInputBlur: commitDraftEmails,
            onInputKeyDown: handleEmailInputKeyDown,
            onRemoveEmail: handleRemoveEmail,
            onSend: sendEmailInvites,
            onClose,
         },
      }),
      [
         inviteLink,
         flowState.isLoadingInviteLink,
         flowState.emailRecipients,
         flowState.emailDraft,
         flowState.invalidEmailEntries,
         flowState.failedEmailEntries,
         flowState.isSendingInvites,
         canSend,
         copyInviteLink,
         onClose,
         handleEmailInputChange,
         commitDraftEmails,
         handleEmailInputKeyDown,
         handleRemoveEmail,
         sendEmailInvites,
         ensureInviteLink,
         formatInviteMessage,
         mergeFlowState,
      ],
   );

   const goToOptionsStep = useCallback(
      () =>
         mergeFlowState({
            currentStep: INVITE_FLOW_STEP.OPTIONS,
            emailDraft: '',
            invalidEmailEntries: [],
            failedEmailEntries: [],
         }),
      [mergeFlowState],
   );

   return { currentStep: flowState.currentStep, goToOptionsStep, stepViewModels };
};
